import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import {
  buildOccurrences,
  CalendarEventActiveRange,
  CalendarEventOccurrence,
} from './calendar.event.mapper';
import { VisitorEvent } from '../../shared/models/visitor.event';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CalendarMonthPdfEntry {
  title: string;
  timeText: string;
  isAllDay: boolean;
  sortValue: number;
  isChoice?: boolean;
  spansMultipleDays?: boolean;
  spanKey?: string;
  spanStartIsoDate?: string;
  spanEndIsoDate?: string;
}

export interface CalendarMonthPdfDay {
  isoDate: string;
  dayOfMonth: number;
  inMonth: boolean;
  isToday: boolean;
  entries: CalendarMonthPdfEntry[];
}

export interface CalendarMonthPdfWeek {
  days: CalendarMonthPdfDay[];
}

export interface CalendarMonthPdfModel {
  monthLabel: string;
  weekdayLabels: string[];
  weeks: CalendarMonthPdfWeek[];
}

export interface BuildCalendarMonthPdfModelCommand {
  events: VisitorEvent[];
  monthStart: Date;
  timezoneFormatter?: string | null;
  activeRange?: CalendarEventActiveRange | null;
  today?: Date;
}

const DEFAULT_TIMEZONE = 'America/New_York';
const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function buildCalendarMonthPdfModel(
  command: BuildCalendarMonthPdfModelCommand
): CalendarMonthPdfModel {
  const timezoneFormatter = command.timezoneFormatter || DEFAULT_TIMEZONE;
  const monthStart = resolveMonthStart(command.monthStart, timezoneFormatter);
  const monthEnd = monthStart.endOf('month');
  const gridStart = monthStart.startOf('week');
  const gridEnd = monthEnd.endOf('week');
  const today = dayjs(command.today || new Date()).tz(timezoneFormatter).startOf('day');
  const dayMap = new Map<string, CalendarMonthPdfDay>();
  const weeks: CalendarMonthPdfWeek[] = [];

  let cursor = gridStart;
  while (cursor.isBefore(gridEnd) || cursor.isSame(gridEnd, 'day')) {
    const weekDays: CalendarMonthPdfDay[] = [];

    for (let index = 0; index < 7; index++) {
      const isoDate = cursor.format('YYYY-MM-DD');
      const day: CalendarMonthPdfDay = {
        isoDate,
        dayOfMonth: Number(cursor.format('D')),
        inMonth: cursor.month() === monthStart.month(),
        isToday: cursor.isSame(today, 'day'),
        entries: [],
      };

      dayMap.set(isoDate, day);
      weekDays.push(day);
      cursor = cursor.add(1, 'day');
    }

    weeks.push({ days: weekDays });
  }

  command.events.forEach((event) => {
    const timezoneForEvent = event.timezoneFormatter || timezoneFormatter;
    buildOccurrences({ ...event, timezoneFormatter: timezoneForEvent })
      .map((occurrence) =>
        command.activeRange
          ? clipOccurrenceToRange(occurrence, command.activeRange, timezoneForEvent)
          : occurrence
      )
      .filter((occurrence): occurrence is CalendarEventOccurrence => !!occurrence)
      .forEach((occurrence) => {
        const clippedOccurrence = clipOccurrenceToMonth(
          occurrence,
          monthStart,
          monthEnd
        );
        if (!clippedOccurrence) return;

        let entryDay = clippedOccurrence.startDay;
        while (
          entryDay.isBefore(clippedOccurrence.endDay) ||
          entryDay.isSame(clippedOccurrence.endDay, 'day')
        ) {
          const day = dayMap.get(entryDay.format('YYYY-MM-DD'));
          if (day) {
            day.entries.push(
              buildDayEntry(event, clippedOccurrence, entryDay)
            );
          }

          entryDay = entryDay.add(1, 'day');
        }
      });
  });

  weeks.forEach((week) =>
    week.days.forEach((day) =>
      day.entries.sort((a, b) =>
        a.sortValue === b.sortValue
          ? a.title.localeCompare(b.title)
          : a.sortValue - b.sortValue
      )
    )
  );

  return {
    monthLabel: monthStart.format('MMMM YYYY'),
    weekdayLabels: WEEKDAY_LABELS,
    weeks,
  };
}

function buildDayEntry(
  event: VisitorEvent,
  occurrence: CalendarEventOccurrence,
  day: dayjs.Dayjs
): CalendarMonthPdfEntry {
  const spansMultipleDays = occurrence.endDay.diff(occurrence.startDay, 'day') > 0;
  const shouldRenderAsSpanBar = spansMultipleDays || occurrence.isAllDay;
  const spanStartIsoDate = occurrence.startDay.format('YYYY-MM-DD');
  const spanEndIsoDate = occurrence.endDay.format('YYYY-MM-DD');
  const spanKey = shouldRenderAsSpanBar
    ? [
        event._id || event.id || event.nameId || event.name || event.title || 'event',
        spanStartIsoDate,
        spanEndIsoDate,
        occurrence.isAllDay ? 'all-day' : occurrence.start.valueOf(),
      ].join('|')
    : undefined;

  return {
    title: event.name || event.title || 'Untitled event',
    timeText: buildDayTimeText(occurrence, day),
    isAllDay: occurrence.isAllDay,
    sortValue: occurrence.isAllDay
      ? day.startOf('day').valueOf() - 1
      : getVisibleStartForDay(occurrence, day).valueOf(),
    isChoice: !!event.isChoice,
    spansMultipleDays,
    spanKey,
    spanStartIsoDate,
    spanEndIsoDate,
  };
}

function buildDayTimeText(
  occurrence: CalendarEventOccurrence,
  day: dayjs.Dayjs
) {
  if (occurrence.isAllDay) return '';

  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');
  const visibleStart = getVisibleStartForDay(occurrence, day);
  const visibleEnd = occurrence.end
    ? occurrence.end.isBefore(dayEnd)
      ? occurrence.end
      : dayEnd
    : visibleStart;
  const startsToday = visibleStart.isAfter(dayStart);
  const endsToday = occurrence.end ? visibleEnd.isBefore(dayEnd) : true;

  if (startsToday && endsToday) {
    return formatTimeRange(visibleStart, visibleEnd);
  }

  if (startsToday) {
    return `${visibleStart.format('h:mm A')} onward`;
  }

  if (endsToday) {
    return `Until ${visibleEnd.format('h:mm A')}`;
  }

  return 'All Day';
}

function getVisibleStartForDay(
  occurrence: CalendarEventOccurrence,
  day: dayjs.Dayjs
) {
  const dayStart = day.startOf('day');
  return occurrence.start.isAfter(dayStart) ? occurrence.start : dayStart;
}

function formatTimeRange(start: dayjs.Dayjs, end: dayjs.Dayjs) {
  const startText = start.format('h:mm A');
  const endText = end.format('h:mm A');
  return startText === endText ? startText : `${startText} - ${endText}`;
}

function clipOccurrenceToRange(
  occurrence: CalendarEventOccurrence,
  activeRange: CalendarEventActiveRange,
  timezoneFormatter: string
) {
  const rangeStart = dayjs(activeRange.from).tz(timezoneFormatter);
  const rangeEnd = dayjs(activeRange.to).tz(timezoneFormatter);
  const occurrenceEnd = occurrence.end || occurrence.start;

  if (occurrenceEnd.isBefore(rangeStart) || occurrence.start.isAfter(rangeEnd)) {
    return null;
  }

  const start = occurrence.start.isBefore(rangeStart) ? rangeStart : occurrence.start;
  const end = occurrence.end
    ? occurrence.end.isAfter(rangeEnd)
      ? rangeEnd
      : occurrence.end
    : null;

  return {
    ...occurrence,
    start,
    end,
    startDay: start.startOf('day'),
    endDay: (end || start).startOf('day'),
  };
}

function resolveMonthStart(monthStart: Date, timezoneFormatter: string) {
  const year = monthStart.getUTCFullYear();
  const month = `${monthStart.getUTCMonth() + 1}`.padStart(2, '0');

  // FullCalendar month view emits UTC-midnight anchors like 2026-03-01T00:00:00Z.
  // If we convert that directly into a western timezone first, it can slip back
  // into the previous month. Using a mid-month wall-clock anchor preserves the
  // intended visible month before normalizing to startOf('month').
  return dayjs
    .tz(`${year}-${month}-15T12:00:00`, timezoneFormatter)
    .startOf('month');
}

function clipOccurrenceToMonth(
  occurrence: CalendarEventOccurrence,
  monthStart: dayjs.Dayjs,
  monthEnd: dayjs.Dayjs
) {
  const monthStartTime = monthStart.startOf('day');
  const monthEndTime = monthEnd.endOf('day');
  const occurrenceEnd = occurrence.end || occurrence.start;

  if (occurrenceEnd.isBefore(monthStartTime) || occurrence.start.isAfter(monthEndTime)) {
    return null;
  }

  const start = occurrence.start.isBefore(monthStartTime)
    ? monthStartTime
    : occurrence.start;
  const end = occurrence.end
    ? occurrence.end.isAfter(monthEndTime)
      ? monthEndTime
      : occurrence.end
    : null;

  return {
    ...occurrence,
    start,
    end,
    startDay: start.startOf('day'),
    endDay: (end || start).startOf('day'),
  };
}
